import { useGraphActivity, type ActivityGroup, type ActivityItem } from '~/hooks/queries/use-graph';
import { formatTime } from '~/lib/local-datetime';
import { describeConflict, displayConflictValue, keyLabel, sourceLabel } from './memory-labels';

/**
 * FRONT-2486: the line a person reads. `body` is the server's log line
 * ("Learned routine.friday_plant_watering = … about X") and stays as the
 * fallback for a server that does not send the parts yet.
 */
export function activityText(item: ActivityItem): string {
  if (item.kind === 'update' && item.key && item.value !== undefined) {
    const about = item.entity_name ? ` about ${item.entity_name}` : '';
    return `${keyLabel(item.key)}${about}: “${item.value}”`;
  }
  if (item.kind === 'flag' && item.key && item.category && item.entity_name) {
    const line = describeConflict({ entity_name: item.entity_name, category: item.category, key: item.key });
    return item.value !== undefined && item.value_b !== undefined
      ? `${line} “${displayConflictValue(item.value)}” vs “${displayConflictValue(item.value_b)}”`
      : line;
  }
  return item.body;
}

function ActivityItemRow({ item }: { item: ActivityItem }) {
  const kindStyles = {
    create: 'bg-success/15 text-success',
    update: 'bg-primary/15 text-primary',
    flag: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  };

  const kindLabels = {
    create: 'NEW',
    update: 'LEARNED',
    flag: 'CONFLICT',
  };

  return (
    <div className="flex items-start gap-3 py-2 border-b border-border last:border-0">
      <span className={`text-[length:var(--text-2xs)] font-medium px-1.5 py-0.5 rounded ${kindStyles[item.kind]}`}>
        {kindLabels[item.kind]}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate">{activityText(item)}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatTime(item.when)} — {sourceLabel(item.who)}
        </p>
      </div>
    </div>
  );
}

function ActivityGroupSection({ group }: { group: ActivityGroup }) {
  return (
    <div className="mb-4">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group.date}</h3>
      <div className="bg-muted rounded-lg px-3">
        {group.items.map((item, i) => (
          <ActivityItemRow key={`${item.when}-${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}

export function ActivityFeed() {
  const { data: activity, isLoading, error } = useGraphActivity(100);

  if (isLoading) {
    return <div className="text-muted-foreground text-sm">Loading activity...</div>;
  }

  if (error) {
    return <div className="text-destructive text-sm">Failed to load activity: {(error as Error).message}</div>;
  }

  if (!activity || activity.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="text-sm">No recent activity</p>
        <p className="text-xs mt-1">Entity creations and learnings will appear here</p>
      </div>
    );
  }

  return (
    <div>
      {activity.map((group) => (
        <ActivityGroupSection key={group.date} group={group} />
      ))}
    </div>
  );
}
